import { PopoverOrigin } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Dropdown from '@/components/switchback/Dropdown/Dropdown';
import { CARET_LARGE } from '@/components/switchback/Icon/assets';
import Icon, { IconSize, IconType } from '@/components/switchback/Icon/IconComponent';
import Loading from '@/components/switchback/Loading';
import Text from '@/components/switchback/Text/Text';
import KEY from '@/constants/key';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useCombinedRefs } from '@/hooks/useCombinedRef';
import { useDebouncedTemporaryValue } from '@/hooks/useDebouncedTemporaryValue';
import useWindowSize from '@/hooks/useWindowSize';
import { generateSimpleId } from '@/utility/helpers';

import css from './Select.module.css';

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = { horizontal: 'left', vertical: 'bottom' };

const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = { horizontal: 'left', vertical: 'top' };

const getOptionId = (prefix: string, value: string) => `${prefix}-option-${value}`;

const findInsideOptions = (text: string, searchStartIndex: number, options: HTMLCollection) => {
  const optionsArray = Array.from(options);

  const foundIndex = optionsArray
    .slice(searchStartIndex)
    .findIndex(el => el.textContent?.toLowerCase().startsWith(text));

  return options.item(foundIndex) || undefined;
};

type TButtonHTMLAttributes = React.ButtonHTMLAttributes<HTMLButtonElement>;
type TSelectHTMLAttributes = React.SelectHTMLAttributes<HTMLSelectElement>;

export type TIcon = {
  name: IconType;
  label?: string;
  className?: string;
};

export interface IOption {
  'aria-label'?: string;
  icon?: TIcon;
  label: string;
  shortLabel?: string;
  value: string;
}

export enum ESelectVariants {
  borderless = 'borderless',
  normal = 'normal',
}

enum ESelectColors {
  gray = 'gray',
}

type TSelectButtonProps = {
  /** button text */
  text?: string;
  icon?: TIcon;
  testId?: string;
  isLoading?: boolean;
  withContainer?: boolean;
  hasError?: boolean;
  isOpen?: boolean;
} & TButtonHTMLAttributes;

/** SelectButton is extracted from Select to be reused for modal toggle */
const _SelectButton = React.forwardRef<HTMLButtonElement, TSelectButtonProps>(function SelectButton(
  {
    text,
    icon,
    isLoading = false,
    testId,
    withContainer = false,
    hasError = false,
    isOpen = false,
    className,
    ...btnProps
  }: TSelectButtonProps,
  ref,
) {
  const Btn = () => (
    <button
      ref={ref}
      type="button"
      data-testid={`${testId}-button`}
      className={`${css.label} ${className || ''}`}
      {...btnProps}>
      <span className="flex items-center mr-1 text-gray-900 text-300 lg:text-500">
        {icon && (
          <span
            className={`flex items-center w-6 h-6 mr-1 ${icon.className}`}
            aria-label={icon.label}
            aria-hidden={!icon.label}>
            <Icon name={icon.name} size={IconSize.normal} aria-hidden />
          </span>
        )}
        {text}
      </span>
      {(isLoading && <Loading embedded className={css.loader} />) || (
        <Icon className={css.icon} name={CARET_LARGE} width={12} />
      )}
    </button>
  );
  if (!withContainer) return <Btn />;
  return (
    <div
      data-opened={String(isOpen)}
      className={css.container}
      data-variant="normal"
      data-has-error={String(hasError)}
      data-is-disabled={String(btnProps.disabled || isLoading)}
      data-native-on-small="true">
      <Btn />
    </div>
  );
});

export const SelectButton = React.memo(_SelectButton);

interface ISelectOnChangeEvent {
  target: {
    value?: string;
    dataset?: DOMStringMap;
  };
  currentTarget: {
    value?: string;
    dataset: DOMStringMap;
  };
}

export type TSelectChangeHandler = (e: ISelectOnChangeEvent) => void;

interface IProps extends Omit<TSelectHTMLAttributes, 'defaultValue' | 'value' | 'onChange'> {
  /**
   * Test id, used for QA automation and tests.
   */
  'data-testid'?: string;
  /**
   * Default initial value for the select.
   * Use this if you don't want to control the component's value.
   */
  defaultValue?: string;
  /**
   * Dropdown footer
   */
  dropdownFooter?: React.ReactNode;
  /**
   * Dropdown header
   */
  dropdownHeader?: React.ReactNode;
  /**
   * Validation error to be displayed
   */
  error?: string;
  /**
   * Classname to apply to the error message element
   */
  errorClassName?: string;
  /**
   * Current value for the select.
   * Use this if you want to control the component's value.
   */
  value?: string;
  /**
   * Select component options to be displayed.
   */
  options: IOption[];
  /**
   * Label of the input
   */
  label?: string;
  /**
   * The origin for where on the select trigger the options list will be displayed.
   */
  anchorOrigin?: PopoverOrigin;
  /**
   * The origin to align the options list itself.
   */
  transformOrigin?: PopoverOrigin;
  /**
   * Use browser's native select on mobile
   */
  nativeOnSmall?: boolean;
  /**
   * Change event handler, needed if you want to control the component's value.
   */
  onChange?: TSelectChangeHandler;
  /**
   * Styling variant
   */
  variant?: ESelectVariants;
  color?: ESelectColors;
  disabled?: boolean;
  isLoading?: boolean;
}

const Select = React.forwardRef<HTMLSelectElement, IProps>(
  (
    {
      'data-testid': testId = 'select',
      id,
      value,
      defaultValue = '',
      dropdownFooter,
      dropdownHeader,
      error,
      errorClassName = '',
      label,
      options,
      anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
      transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
      onChange,
      nativeOnSmall = true,
      variant = 'normal',
      color,
      disabled = false,
      isLoading,
      ...props
    },
    ref,
  ) => {
    const windowSize = useWindowSize();
    const { isMobile } = useBreakpoint();
    // @ts-expect-error fixable: unchecked index access
    const [internalValue, setInternalValue] = useState(defaultValue || options[0].value);
    const inputId = useMemo(() => id || `input_${generateSimpleId()}`, [id]);
    const [isOpen, setIsOpen] = useState(false);
    const [contentWidth, setContentWidth] = useState(0);
    const isControlled = typeof value !== 'undefined';
    const currentValue = isControlled ? value : internalValue;
    const currentLabel = useMemo(
      () => options.find(({ value }) => currentValue === value)?.label,
      [options, currentValue],
    );
    const currentIcon = useMemo(
      () => options.find(({ value }) => currentValue === value)?.icon,
      [options, currentValue],
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const customListboxRef = useRef<HTMLUListElement | null>(null);
    const customButtonRef = useRef<HTMLButtonElement>(null);
    const customSelectedOptionRef = useRef<HTMLLIElement | null>(null);
    const innerSelectRef = useRef<HTMLSelectElement>(null);
    const combinedSelectRef = useCombinedRefs<HTMLSelectElement>(ref, innerSelectRef);
    const customSelectId = useMemo(() => `custom-select-${generateSimpleId()}`, []);
    const customButtonId = `${customSelectId}-button`;
    const [, setTemporarySearch] = useDebouncedTemporaryValue('');
    const isDisabled = !!disabled || !!isLoading;

    const handleNativeChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        isControlled ? onChange?.(e) : setInternalValue(e.currentTarget.value);
      },
      [isControlled, onChange],
    );

    const handleKeyboardEvent = useCallback(
      <E extends React.KeyboardEvent<HTMLElement>>(
        callback: (event: E) => void,
        allowTabKey = false,
      ) =>
        (event: E): void => {
          const isTabPressed = event.key === KEY.TAB;
          /**
           * For the shift key we check for anything other than shift+tab to prevent
           * backwards tabbing if `allowTabKey` is false.
           */
          const isModifierPressed =
            event.altKey || event.ctrlKey || event.metaKey || (event.shiftKey && !isTabPressed);
          if ((isMobile && nativeOnSmall) || (allowTabKey && isTabPressed) || isModifierPressed) {
            return;
          }

          event.preventDefault();
          callback(event);
        },
      [isMobile, nativeOnSmall],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleCustomKeyboardTrigger = useCallback(
      handleKeyboardEvent(e => {
        if (e.key === KEY.SPACE || e.key === KEY.DOWN || e.key === KEY.ENTER) {
          setIsOpen(true);
        }
      }, true),
      [handleKeyboardEvent],
    );

    const handleCustomButtonClick = useCallback(
      () => (!isMobile || !nativeOnSmall) && setIsOpen(open => !open),
      [isMobile, nativeOnSmall],
    );

    const handleEntered = useCallback(() => customListboxRef.current?.focus(), []);

    const handleDismiss = useCallback(() => {
      customButtonRef.current?.focus();
      setIsOpen(false);
    }, []);

    const handleExited = useCallback(() => customButtonRef.current?.focus(), []);

    const getCurrentFocusedOptionIndex = useCallback(
      (e: React.SyntheticEvent<HTMLElement>): number => {
        const focusedElement =
          document.activeElement !== customListboxRef.current
            ? document.activeElement
            : customSelectedOptionRef.current;
        const currentFocus = [].findIndex.call(
          e.currentTarget.children,
          (el: HTMLElement) => el === focusedElement,
        );
        return currentFocus;
      },
      [],
    );
    const handleCustomChange = useCallback(
      (newValue: string) => {
        if (isControlled) {
          if (combinedSelectRef.current) {
            combinedSelectRef.current.value = newValue;
            combinedSelectRef.current.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          setInternalValue(newValue);
        }
        setIsOpen(false);
        customButtonRef.current?.focus();
      },
      [isControlled, combinedSelectRef],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleCustomKeyboardNavigation = useCallback<
      React.KeyboardEventHandler<HTMLUListElement>
    >(
      handleKeyboardEvent(e => {
        const options = e.currentTarget.children;
        switch (e.key) {
          case KEY.DOWN: {
            const currentFocusIndex = getCurrentFocusedOptionIndex(e);
            const nextFocusIndex =
              currentFocusIndex !== options.length - 1 ? currentFocusIndex + 1 : 0;
            // @ts-expect-error fixable: unchecked index access
            options[nextFocusIndex].focus();
            break;
          }
          case KEY.UP: {
            const currentFocusIndex = getCurrentFocusedOptionIndex(e);
            const nextFocusIndex =
              currentFocusIndex !== 0 ? currentFocusIndex - 1 : options.length - 1;
            // @ts-expect-error fixable: unchecked index access
            options[nextFocusIndex].focus();
            break;
          }
          case KEY.HOME:
            // @ts-expect-error fixable: unchecked index access
            options[0].focus();
            break;
          case KEY.END:
            // @ts-expect-error fixable: unchecked index access
            options[options.length - 1].focus();
            break;
          case KEY.ESCAPE:
            setIsOpen(false);
            break;
          case KEY.SPACE:
          case KEY.ENTER:
            handleCustomChange((e.target as HTMLElement).dataset.value as string);
            break;
          default: {
            /**
             * Storing/using event values outside of the update function to avoid
             * losing the event object because of async issues.
             */
            const key = e.key;
            const currentFocusIndex = getCurrentFocusedOptionIndex(e);
            /**
             * Using the setter function with a functional update style to avoid
             * losing track of the search value and to maintain the current function
             * cached.
             */
            setTemporarySearch((search: string) => {
              /**
               * If search has a value this means we're contuining a previous search,
               * in that case let's avoid jumping the current focus if there's still a match.
               * If search has no value we're starting a search, so let's skip the current focus.
               */
              const searchStartIndex = search ? currentFocusIndex : currentFocusIndex + 1;
              const newSearch = search + key;
              /**
               * Perform a new search with all options if the previous search with
               * a start index returns nothing.
               */
              const match =
                findInsideOptions(newSearch, searchStartIndex, options) ??
                findInsideOptions(newSearch, 0, options);

              if (match?.tagName === 'LI') {
                (match as HTMLLIElement).focus();
              }

              return newSearch;
            });
            break;
          }
        }
      }, !nativeOnSmall && isMobile),
      [handleKeyboardEvent, handleCustomChange],
    );

    const handleCustomOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
      const value = e.currentTarget.dataset.value;
      if (typeof value === 'string') {
        handleCustomChange(value);
      }
    };

    /**
     * Set desktop popup position and size
     */
    useEffect(() => {
      const anchor = containerRef.current;

      if (!anchor || !windowSize.height || !options.length) {
        return;
      }

      const { width } = anchor.getBoundingClientRect();

      setContentWidth(width - 48);
    }, [windowSize, setContentWidth, options]);

    useEffect(() => {
      if (isDisabled && isOpen) {
        setIsOpen(false);
      }
    }, [isDisabled, isOpen]);

    return (
      <div
        data-testid={testId}
        data-opened={String(isOpen)}
        className={css.container}
        data-variant={variant}
        {...(color && { 'data-color': color })}
        data-has-label={String(!!label)}
        data-has-error={String(!!error)}
        data-is-disabled={String(isDisabled)}
        data-native-on-small={String(nativeOnSmall)}
        ref={containerRef}>
        {label && (
          <label className={css.selectLabel} htmlFor={inputId}>
            {label}
          </label>
        )}
        <select
          id={inputId}
          name={inputId}
          className={css.select}
          value={currentValue}
          ref={combinedSelectRef}
          disabled={isDisabled}
          {...props}
          onChange={handleNativeChange}>
          {options.map(({ label, value }) => (
            <option
              key={value}
              className="text-gray-900"
              value={value}
              data-selected={String(currentValue === value)}>
              {label}
            </option>
          ))}
        </select>
        <SelectButton
          ref={customButtonRef}
          id={customButtonId}
          aria-hidden={isMobile && nativeOnSmall}
          aria-label={props['aria-label']}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          tabIndex={isMobile && nativeOnSmall ? -1 : 0}
          onClick={handleCustomButtonClick}
          onKeyDown={handleCustomKeyboardTrigger}
          type="button"
          disabled={isDisabled}
          icon={currentIcon}
          text={currentLabel}
          isLoading={isLoading}
          testId={testId}
        />
        <Dropdown
          className="px-6 py-5 my-1 rounded shadow-200"
          customHeader={dropdownHeader}
          customFooter={dropdownFooter}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableEscapeKeyDown
          show={isOpen}
          relativeTo={containerRef.current}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onEntered={handleEntered}
          onDismiss={handleDismiss}
          onExited={handleExited}>
          <ul
            ref={customListboxRef}
            data-testid={`${testId}-dropdown`}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={customButtonId}
            aria-activedescendant={getOptionId(customSelectId, currentValue)}
            className="my-2 font-normal text-gray-900 bg-white text-300 leading-300 text"
            style={{ minWidth: contentWidth }}
            onKeyDown={handleCustomKeyboardNavigation}>
            {options.map(({ label, value, icon, ['aria-label']: ariaLabel }) => {
              const isSelected = currentValue === value;
              return (
                <li // eslint-disable-line jsx-a11y/click-events-have-key-events
                  aria-label={ariaLabel}
                  ref={isSelected ? customSelectedOptionRef : undefined}
                  key={value}
                  id={getOptionId(customSelectId, value)}
                  role="option"
                  tabIndex={0}
                  aria-selected={isSelected}
                  data-value={value}
                  className={css.customOption}
                  onClick={handleCustomOptionClick}>
                  {icon && (
                    <span
                      className={`flex items-center w-6 h-6 mr-1 ${icon.className}`}
                      aria-label={icon.label}
                      aria-hidden={!icon.label}>
                      <Icon name={icon.name} size={IconSize.normal} aria-hidden />
                    </span>
                  )}
                  {label}
                </li>
              );
            })}
          </ul>
        </Dropdown>
        {error && (
          <Text
            data-testid={`${props.name}-input-error`}
            type="inline"
            className={`${css.error} ${errorClassName}`}>
            {error}
          </Text>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
